import {
  updateAction, setFocusedNodeId, setActiveNodeEditorTab,
  updateThreshold,
} from '../slice';
import type { AppDispatch, State } from '~/store';
import { selectActionByPublicId, selectCustomListByActionPublicId, selectThresholds } from '../selectors';
import {
  CUSTOM_LIST_COLUMNS_PLACEHOLDER,
  CUSTOM_LIST_SCORE_PLACEHOLDER,
  checkIfSelectedContainsPII,
} from 'flow/service/actions/services/customLists/customLists';
import {
  CUSTOM_LIST_COLUMNS_OPTION_KEY,
  CUSTOM_LIST_VERSION_OPTION_KEY,
} from 'flow/service/actions/services/customLists/constants';

/**
 * Updates a Custom List service action option major version to the active version
 * If the action is already using the active version, don't update action
 * If the action is updating to the active version:
 * -> update the major version option to the active version
 * -> filter the selected columns to only include active columns or default to 'all'
 * -> if the selected columns contain PII and wasn't using exact match, update to exact match (1)
 * we open the action in the drawer with the dependents tab focused
 */
export function updateCustomListVersionToActiveThunk(actionPublicId: string) {
  return (dispatch: AppDispatch, getState: () => State) => {
    const state = getState();
    const targetAction = selectActionByPublicId(actionPublicId)(state);
    const customListVersions = selectCustomListByActionPublicId(actionPublicId)(state);
    const thresholds = selectThresholds(state);

    if (!customListVersions || !targetAction) return;
    const { currentVersion, activeVersion } = customListVersions;
    if (!currentVersion || !activeVersion) return;

    if (currentVersion.major_version !== activeVersion.major_version) {
      // Update both custom_list_major_version and version_columns action options
      const currentOptions = targetAction.options;
      const updatedOptions = { ...currentOptions };

      // Update custom_list_major_version option
      if (currentOptions[CUSTOM_LIST_VERSION_OPTION_KEY]) {
        updatedOptions[CUSTOM_LIST_VERSION_OPTION_KEY] = {
          ...currentOptions[CUSTOM_LIST_VERSION_OPTION_KEY],
          value: String(customListVersions.activeVersion.major_version),
        };
      }

      // Update version_columns option
      if (currentOptions[CUSTOM_LIST_COLUMNS_OPTION_KEY]) {
        updatedOptions[CUSTOM_LIST_COLUMNS_OPTION_KEY] = {
          ...currentOptions[CUSTOM_LIST_COLUMNS_OPTION_KEY],
          value: '',
          jsonValue: customListVersions.activeVersion.columns,
        };
      }

      // Dispatch once with all updated options
      dispatch(updateAction({
        path: `${targetAction.publicId}.options`,
        value: updatedOptions,
      }));

      const activeColumnValues = customListVersions.activeVersion.columns.map(
        column => column.name,
      );
      const activeColumns = customListVersions.activeVersion.columns;

      // find any dependencies where the target action is the left side
      Object.values(thresholds)
        .filter(threshold => threshold.left.actionId === targetAction.publicId)
        .forEach(dependency => {
          const columnsComponentIndex = dependency.left.attributeComponents.findIndex(
            c => c.attributeComponent.placeholder === CUSTOM_LIST_COLUMNS_PLACEHOLDER,
          );

          const selectedColumns = dependency.left.attributeComponents[
            columnsComponentIndex
          ]?.value;

          // need to make sure to filter out non active columns
          if (selectedColumns && Array.isArray(selectedColumns)) {
            const matchedColumns = activeColumnValues.filter(
              column => selectedColumns.includes(column),
            );

            // if no columns matched, default to 'all'
            if (matchedColumns.length === 0) {
              matchedColumns.push('all');
            }

            dispatch(updateThreshold({
              path: `${dependency.publicId}.left.attributeComponents[${columnsComponentIndex}].value`,
              value: matchedColumns,
            }));

            // ======== For encryption and update dependency scores to exact match ========

            // When the selected columns now contain PII and wasn't using exact match:
            const shouldUpdateToExactMatch = checkIfSelectedContainsPII(
              matchedColumns, activeColumns,
            );

            if (shouldUpdateToExactMatch) {
              const scoreComponentIndex = dependency.left.attributeComponents.findIndex(
                c => c.attributeComponent.placeholder === CUSTOM_LIST_SCORE_PLACEHOLDER,
              );

              const currentScore = dependency.left.attributeComponents[scoreComponentIndex]?.value;

              if (scoreComponentIndex !== -1 && currentScore !== '1') {
                dispatch(updateThreshold({
                  path: `${dependency.publicId}.left.attributeComponents[${scoreComponentIndex}].value`,
                  value: '1',
                }));
              }
            }
          }
        });

      dispatch(setFocusedNodeId(targetAction.publicId));
      // We need to wait for the action to be updated before we can set the focused tab
      setTimeout(() => {
        dispatch(setActiveNodeEditorTab('dependents'));
      }, 0);
    }
  };
}
